import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { axiosApi } from '@/api/axios';
import { useAuthStore } from '@/store';
import { groupChartData } from '@/utils';

import {
    ChangeUserDataBody,
    ChangeUserDataResponse,
    GetOperationHistoryResponse,
    GetUserResponse,
    GetUserSalesChartDataResponse,
} from './types';

export const useGetUser = () => {
    const { token, setIsAuth } = useAuthStore();
    return useQuery({
        enabled: !!token,
        queryKey: ['user', token],
        queryFn: async () => {
            const { data: userData } = await axiosApi.get<GetUserResponse>('user', {
                headers: { Authorization: `Bearer ${token}` },
            });
            setIsAuth(true);
            return userData;
        },
    });
};

export const useChangeUserData = () => {
    const { token } = useAuthStore();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: ChangeUserDataBody) => {
            const { data: res, status } = await axiosApi.patch<ChangeUserDataResponse>(
                'user',
                data,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    validateStatus: status => status < 500,
                }
            );

            if (status === 200) {
                if (!Array.isArray(res.data)) {
                    localStorage.setItem('timestamp', String(res.data.resendCooldown) + '000');
                } else {
                    queryClient.invalidateQueries({ queryKey: ['user', token] });
                }
            }

            if (status >= 400) {
                if (status === 429) {
                    const targetError = res.errors?.find(err => err.type === 'cooldown');
                    localStorage.setItem(
                        'timestamp',
                        String(targetError?.data.resendCooldown) + '000'
                    );
                }
                return res?.errors;
            }
        },
    });
};

export const useDeleteUser = () => {
    const { token, clearAuth } = useAuthStore();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            await axiosApi.delete('user', {
                headers: { Authorization: `Bearer ${token}` },
            });

            clearAuth();
            queryClient.removeQueries({ queryKey: ['user', token] });
        },
    });
};

export const useGetOperationHistory = (page: number = 1) => {
    const { token } = useAuthStore();

    return useQuery({
        enabled: !!token,
        queryKey: ['operation-history', token, page],
        queryFn: async () => {
            const { data } = await axiosApi.get<GetOperationHistoryResponse>(
                `user/operations?page=${page}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            return data.data;
        },
    });
};

export const useGetUserSalesChartData = () => {
    const { token } = useAuthStore();

    return useQuery({
        enabled: !!token,
        queryKey: ['sales-chart-data', token],
        queryFn: async () => {
            const {
                data: {
                    data: { operations },
                },
            } = await axiosApi.get<GetUserSalesChartDataResponse>('user/operations/chart/sales', {
                headers: { Authorization: `Bearer ${token}` },
            });

            return groupChartData(operations);
        },
    });
};
